import { Injectable, computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslationService } from 'eshava-shared';
import { map } from 'rxjs';


export const HELP_PARAM = 'help';

/**
 * Inline help, held in a query parameter rather than in component state.
 *
 * That makes an explanation link shareable, lets the back button close the flyout, and keeps the
 * route structure untouched. It is also the reason the flyout is not a modal: help is read *while*
 * filling something in, so nothing here traps focus or locks scrolling.
 */
@Injectable({ providedIn: 'root' })
export class HelpService {
  private readonly router = inject(Router);
  private readonly translations = inject(TranslationService);

  private readonly activeKey = toSignal(
    this.router.routerState.root.queryParamMap.pipe(map(parameters => parameters.get(HELP_PARAM))),
    { initialValue: null }
  );

  readonly isOpen = computed(() => this.activeKey() !== null);

  readonly text = computed(() => {
    const key = this.activeKey();
    return key === null ? null : this.translations.translate(key);
  });

  /**
   * Whether help exists for a key. The question-mark icon appears only where it does -- which is
   * why `TranslationService.has` exists next to `translate`: the latter falls back to the key and
   * could never answer this.
   */
  hasHelp(key: string): boolean {
    return this.translations.has(key);
  }

  open(key: string): void {
    void this.router.navigate([], {
      queryParams: { [HELP_PARAM]: key },
      queryParamsHandling: 'merge'
    });
  }

  close(): void {
    void this.router.navigate([], {
      queryParams: { [HELP_PARAM]: null },
      queryParamsHandling: 'merge'
    });
  }
}